import React, { useState, useRef, useEffect } from 'react';

interface InjectedProps {
  wrapperRef: React.RefObject<HTMLElement>;
  toggleRef: React.RefObject<HTMLElement>;
  open: boolean;
  hide: () => void;
  show: () => void;
  toggle: () => void;
}

export function ContextMenuHOC<P extends InjectedProps>(
  WrappedComponent: React.ComponentType<P>
) {
  return (props: Omit<P, keyof InjectedProps>) => {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLElement>(null);
    const toggleRef = useRef<HTMLElement>(null);

    const hide = () => {
      setOpen(false);
    };

    const show = () => {
      setOpen(true);
    };

    const toggle = () => {
      setOpen((prev) => !prev);
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (
        open &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target as Node)
      ) {
        hide();
      }
    };

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.keyCode === 27 && open) {
        hide();
      }
    };

    useEffect(() => {
      if (open) {
        document.addEventListener('keydown', handleEscKey);
        document.addEventListener('mousedown', handleClickOutside);
      } else {
        document.removeEventListener('keydown', handleEscKey);
        document.removeEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('keydown', handleEscKey);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [open]);

    return (
      <WrappedComponent
        {...(props as P)}
        wrapperRef={wrapperRef}
        toggleRef={toggleRef}
        open={open}
        hide={hide}
        show={show}
        toggle={toggle}
      />
    );
  };
}
